import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Spinner from "./Spinner";
import type { Partner } from "./AddTransactionForm";

interface PartnerRow extends Partner {
  transaction_count: number;
}

interface Draft {
  name: string;
  email: string;
  nominee: string;
}

const BLANK: Draft = { name: "", email: "", nominee: "" };

function errorText(e: any): string {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => `${(d.loc ?? []).filter((l: any) => l !== "body").join(".") || "form"}: ${String(d.msg).replace(/^Value error, /, "")}`)
      .join("\n");
  }
  if (typeof detail === "string") return detail;
  return "Request failed. Please try again.";
}

export default function PartnersManager() {
  const [rows, setRows] = useState<PartnerRow[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<PartnerRow[]>("/partners");
      setRows(data);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForm = (row?: PartnerRow) => {
    setError(null);
    setNotice(null);
    setEditId(row?.id ?? null);
    setDraft(row ? { name: row.name, email: row.email, nominee: row.nominee } : BLANK);
  };

  const closeForm = () => { setDraft(null); setEditId(null); setError(null); };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      if (editId) {
        const { data } = await api.put<PartnerRow>(`/partners/${encodeURIComponent(editId)}`, {
          name: draft.name,
          nominee: draft.nominee,
        });
        setNotice(`Updated ${data.name}.` + (data.transaction_count ? ` ${data.transaction_count} transaction(s) now show the new name.` : ""));
      } else {
        const { data } = await api.post<PartnerRow>("/partners", draft);
        setNotice(`Added ${data.name}.`);
      }
      closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: PartnerRow) => {
    if (row.transaction_count > 0) {
      setNotice(null);
      setError(`${row.name} has ${row.transaction_count} transaction(s). Delete those first.`);
      return;
    }
    if (!window.confirm(`Delete partner ${row.name} (${row.email})?\n\nThis can't be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/partners/${encodeURIComponent(row.id)}`);
      setNotice(`Deleted ${row.name}.`);
      if (editId === row.id) closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.card}>
      <div style={S.header}>
        <h3 style={S.title}>Partners {rows && <span style={S.count}>({rows.length})</span>}</h3>
        {!draft && <button style={S.primaryBtn} onClick={() => openForm()}>Add partner</button>}
      </div>

      {draft && (
        <div style={S.form}>
          <label style={S.field}>
            <span style={S.label}>Partner name</span>
            <input style={S.input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label style={S.field}>
            <span style={S.label}>Email</span>
            <input
              style={{ ...S.input, ...(editId ? S.readOnly : {}) }}
              type="email"
              value={draft.email}
              readOnly={!!editId}
              title={editId ? "The email identifies the partner and can't be changed" : undefined}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </label>
          <label style={S.field}>
            <span style={S.label}>Nominee name</span>
            <input style={S.input} value={draft.nominee} onChange={(e) => setDraft({ ...draft, nominee: e.target.value })} />
          </label>
          <div style={S.formActions}>
            <button style={{ ...S.primaryBtn, ...(busy ? S.disabled : {}) }} disabled={busy} onClick={save}>
              {busy ? "Saving…" : editId ? "Save changes" : "Add partner"}
            </button>
            <button style={S.secondaryBtn} disabled={busy} onClick={closeForm}>Cancel</button>
          </div>
          {editId && (
            <p style={S.hint}>
              The email identifies the partner and can't be changed. A new name is also applied to their transactions.
            </p>
          )}
        </div>
      )}

      {notice && <div style={S.success}>{notice}</div>}
      {error && <pre style={S.error}>{error}</pre>}

      {rows === null && !error ? <Spinner /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Name", "Email", "Nominee", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map((r, i) => (
                <tr key={r.id} style={{
                  ...(i % 2 ? { background: "rgba(255,255,255,0.02)" } : {}),
                  ...(editId === r.id ? { background: "rgba(29,158,117,0.10)" } : {}),
                }}>
                  <td style={{ ...S.td, fontWeight: 500 }}>{r.name}</td>
                  <td style={{ ...S.td, color: "var(--text-secondary)" }}>{r.email}</td>
                  <td style={S.td}>{r.nominee || <span style={{ color: "var(--text-muted)" }}>-</span>}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    <button style={S.rowBtn} disabled={busy} onClick={() => openForm(r)}>Edit</button>
                    <button
                      style={{ ...S.rowBtn, ...S.deleteBtn, ...(r.transaction_count ? S.disabled : {}) }}
                      disabled={busy}
                      title={r.transaction_count ? "Partners with transactions can't be deleted" : undefined}
                      onClick={() => remove(r)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {rows?.length === 0 && (
                <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={4}>No partners yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" },
  count: { color: "var(--text-muted)", fontWeight: 400 },
  form: {
    display: "grid", gap: 16, marginBottom: 20, padding: 16, borderRadius: 8,
    background: "rgba(255,255,255,0.03)",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
    textTransform: "uppercase", color: "var(--text-muted)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "10px 12px",
    color: "var(--text-primary)", fontSize: 14, width: "100%",
  },
  readOnly: { opacity: 0.6, cursor: "not-allowed" },
  formActions: { display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" },
  hint: { gridColumn: "1 / -1", fontSize: 12, color: "var(--text-muted)" },
  primaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  secondaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.2)", color: "var(--text-primary)",
  },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 },
  th: {
    textAlign: "left", padding: "10px 12px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px", color: "var(--text-primary)",
    borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap",
  },
  rowBtn: {
    padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)", marginLeft: 6,
  },
  deleteBtn: { borderColor: "rgba(224,90,90,0.4)", color: "#f08a8a" },
  success: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  error: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13, fontFamily: "var(--font-body)", whiteSpace: "pre-wrap",
  },
};
